import d from "./DOM.js";
import { withCsrf } from "./csrf.js";
import Preferences from "./preferences.js";
import Toast from "./toast.js";

export default class Profile {
	public static init() {
		d.e('click', Profile.handleDeleteData, d.qsa('.delete-data-btn'));
		d.e('click', Profile.handleDeleteAccount, d.qsa('.delete-account-btn'));
		d.e('click', Profile.handleDeleteUserAccount, d.qs('#delete-account-btn'));
		d.e('click', () => { window.location.href = '/pricing'; }, d.qs('#change-subscription-btn'));
		d.e('click', Profile.handleManageBilling, d.qs('#manage-billing-btn'));
		d.e('change', Profile.handleDateFormatChange, d.qs('#dateFormatSelect'));
		Profile.initInspirationToggle();
		
		// Theme Selection
		const themeCards = d.qsa('.theme-card');
		const currentTheme = localStorage.getItem('theme') || 'original';
		
		themeCards.forEach(card => {
			if (card.dataset.theme === currentTheme) {
				card.classList.add('active');
			}
			
			d.e('click', () => {
				Profile.setTheme(card.dataset.theme || 'original');
				themeCards.forEach(c => c.classList.remove('active'));
				card.classList.add('active');
			}, card);
		});
	}

	private static async initInspirationToggle() {
		const toggle = d.qs<HTMLInputElement>('#showInspirationToggle');
		if (!toggle) { return; }
		
		const prefs = await Preferences.get();
		toggle.checked = prefs.showInspiration !== false;
		
		toggle.addEventListener('change', async () => {
			const saved = await Preferences.save({ showInspiration: toggle.checked });
			if (saved) {
				Toast.show(toggle.checked ? 'Motivational pop-ups on' : 'Motivational pop-ups off', 'success', 2500);
			} else {
				toggle.checked = !toggle.checked;
				Toast.show('Could not save that setting', 'error');
			}
		});
	}

	public static setTheme(theme: string) {
		// Remove all possible theme classes
		const themes = ['original', 'neon', 'bold', 'light', 'tradingview', 'midnight', 'emerald', 'matrix'];
		themes.forEach(t => document.body.classList.remove(`theme-${t}`));
		
		// Add new theme class
		document.body.classList.add(`theme-${theme}`);
		
		// Save to local cache
		localStorage.setItem('theme', theme);
	}

	public static applySavedTheme() {
		const theme = localStorage.getItem('theme') || 'original';
		document.body.classList.add(`theme-${theme}`);
	}
	
	private static async handleDeleteUserAccount() {
		if (confirm('Are you sure you want to delete your user account? This action cannot be undone.\n\nPlease note: There are no refunds for unused subscription time.')) {
			try {
				const res = await fetch('/api/profile/delete', { method: 'POST', headers: withCsrf() });

				if (res.ok) {
					alert('Your account has been deleted. You will now be logged out.');
					// Perform logout
					await fetch('/auth/logout', { method: 'POST', headers: withCsrf() });
					window.location.href = '/';
				} else {
					alert('Failed to delete account.');
				}
			} catch (err) {
				console.error(err);
				alert('Error deleting account.');
			}
		}
	}
	
	private static async handleDateFormatChange(e: Event) {
		const select = e.target as HTMLSelectElement;
		const dateFormat = select.value;
		
		try {
			const res = await fetch('/api/profile/update', {
				method: 'POST',
				headers: withCsrf({ 'Content-Type': 'application/json' }),
				body: JSON.stringify({ dateFormat })
			});
			
			if (res.ok) {
				// Reload to apply changes (e.g. re-render dates)
				window.location.reload();
			} else {
				alert('Failed to update preference');
			}
		} catch (err) {
			console.error(err);
			alert('Error updating preference');
		}
	}
	
	private static async handleManageBilling() {
		const btn = d.qs<HTMLButtonElement>('#manage-billing-btn');
		
		if (btn) {
			btn.disabled = true;
			btn.textContent = 'Loading...';
		}
		
		try {
			const res = await fetch('/api/subscription/portal', {
				method: 'POST',
				headers: withCsrf({ 'Content-Type': 'application/json' })
			});
			
			const data = await res.json();
			
			if (data.url) {
				window.location.href = data.url;
			} else {
				alert('Failed to open billing portal');
				
				if (btn) {
					btn.disabled = false;
					btn.textContent = 'Manage Billing';
				}
			}
		} catch (err) {
			console.error(err);
			alert('Error opening billing portal');
			
			if (btn) {
				btn.disabled = false;
				btn.textContent = 'Manage Billing';
			}
		}
	}
	
	private static async handleDeleteData(e: Event) {
		const btn = e.target as HTMLButtonElement;
		const account = btn.dataset.account;
		if (!account) { return; }
		
		if (confirm(`Are you sure you want to clear all trade data for account ${account}? This cannot be undone.`)) {
			try {
				const res = await fetch(`/api/accounts/${account}/data`, { method: 'DELETE', headers: withCsrf() });
				
				if (res.ok) {
					alert('Trade data cleared successfully.');
					window.location.reload();
				} else {
					alert('Failed to clear trade data.');
				}
			} catch (err) {
				console.error(err);
				alert('Error clearing trade data.');
			}
		}
	}
	
	private static async handleDeleteAccount(e: Event) {
		const btn = e.target as HTMLButtonElement;
		const account = btn.dataset.account;
		if (!account) { return; }
		
		if (confirm(`Are you sure you want to delete account ${account}? This will hide the account and its data.`)) {
			try {
				const res = await fetch(`/api/accounts/${account}`, { method: 'DELETE', headers: withCsrf() });
				
				if (res.ok) {
					alert('Account deleted successfully.');
					window.location.reload();
				} else {
					alert('Failed to delete account.');
				}
			} catch (err) {
				console.error(err);
				alert('Error deleting account.');
			}
		}
	}
}
